import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon.svg';
import { ReactComponent as CardIcon } from '../../assets/icons/credit-card.svg';
import { ReactComponent as DocumentIcon } from '../../assets/icons/document.svg';
import { ReactComponent as EyeFillIcon } from '../../assets/icons/eye-fill-icon.svg';
import { ReactComponent as LightningIcon } from '../../assets/icons/flash-icon.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { ReactComponent as DocumentFileIcon } from '../../assets/icons/tables/document-file.svg';
import {
  ChargeOrder,
  PaymentAttemptsResponse,
  PaymentTimelineItem,
} from '../../interface/emsp-insights-interface';
import { InvoiceInterface } from '../../interface/invoice-interface';
import InvoiceSidePanel from '../../pages/emsp-invoice/components/InvoiceSidePanel';
import downloadSingleInvoicePdf from '../../pages/emsp-invoice/utils/generateInvoicePdf';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import invoiceApi from '../../utils/api/invoice';
import emspCdrApi from '../../utils/api/v2/emsp-cdr';
import emspSessionApi from '../../utils/api/v2/emsp-session';
import emspTransactionApi from '../../utils/api/v2/emsp-transaction';
import MyButton from '../button/Button';
import Copy from '../copy/Copy';
import ConfirmBox from '../modal-box/ConfirmBox';
import NoDataAvailable from '../no-data-available/NoDataAvailable';
import TabToggle from '../tab-toggle/TabToggle';

import EmspSessionSidePanel from './EmspSessionSidePanel';
import SidePaneModel from './SidePaneModel';

interface EmspCdrSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  onActionSuccess?: () => void;
  onSuccess?: () => void;
  apiSource?: 'transaction' | 'commercialSession';
}

const MONGO_OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

/**
 * Checks whether a candidate transaction record matches the normalized identifier.
 * @param {any} item - Candidate transaction record from the CDR list endpoint.
 * @param {string} normalizedIdentifier - Lowercased, trimmed identifier to match.
 * @returns {boolean} True when any identifier field on the record matches.
 */
const doesCdrRecordMatchIdentifier = (item: any, normalizedIdentifier: string): boolean => {
  const candidateValues = [
    item?.externalId,
    item?.cdrId,
    item?.sessionId,
    item?._id,
    item?.id,
    item?.cdrDetails?.id,
    item?.cdrDetails?.sessionId,
  ];

  for (let index = 0; index < candidateValues.length; index += 1) {
    const candidate = candidateValues[index];
    if (typeof candidate === 'string' && candidate.trim().toLowerCase() === normalizedIdentifier) {
      return true;
    }
  }

  return false;
};

/**
 * Extracts the MongoDB CDR document ID matching the given identifier from a transaction list response.
 * @param {any} responsePayload - Raw API response payload from the commercial transaction list endpoint.
 * @param {string} identifier - Target CDR or session identifier to match against.
 * @returns {string | null} Matched MongoDB CDR ID or null when no matching record exists.
 */
const extractMatchedCdrObjectId = (
  responsePayload: any,
  identifier: string,
): string | null => {
  const results =
    responsePayload?.data?.results ||
    responsePayload?.results ||
    (Array.isArray(responsePayload?.data) ? responsePayload.data : []);

  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  const normalizedIdentifier = identifier.trim().toLowerCase();
  for (let index = 0; index < results.length; index += 1) {
    const item = results[index];
    if (doesCdrRecordMatchIdentifier(item, normalizedIdentifier)) {
      return item?._id || item?.id || null;
    }
  }

  return null;
};

/**
 * Resolves an external OCPI CDR UUID (or session identifier) to its MongoDB CDR document ID.
 * @param {string} identifier - External CDR UUID or identifier from the table row.
 * @returns {Promise<string | null>} Resolved MongoDB document ID, or null if not found.
 */
const resolveCdrObjectId = async (identifier: string): Promise<string | null> => {
  const trimmedIdentifier = identifier.trim();
  if (!trimmedIdentifier) {
    return null;
  }

  try {
    const searchByResponse = await emspTransactionApi.fetchCommercialTransactionList({
      searchBy: trimmedIdentifier,
      limit: 10,
    });
    if (!searchByResponse) {
      return null;
    }
    const matchedBySearchBy = extractMatchedCdrObjectId(searchByResponse, trimmedIdentifier);
    if (matchedBySearchBy) {
      return matchedBySearchBy;
    }
  } catch {
    // Ignore and fall back to alternate search query parameter
  }

  try {
    const searchResponse = await emspTransactionApi.fetchCommercialTransactionList({
      search: trimmedIdentifier,
      limit: 10,
    });
    const matchedBySearch = extractMatchedCdrObjectId(searchResponse, trimmedIdentifier);
    if (matchedBySearch) {
      return matchedBySearch;
    }
  } catch {
    // Ignore resolution errors and allow caller to handle fallback
  }

  return null;
};

/**
 * EmspCdrSidePanel component displaying details for an eMSP CDR.
 * @param {EmspCdrSidePanelProperties} properties - Component properties.
 * @returns {React.JSX.Element} The rendered side panel.
 */
function EmspCdrSidePanel(properties: EmspCdrSidePanelProperties): React.JSX.Element {
  const { t, i18n } = useTranslation('sidePanel');
  const { fetchIdFrom, id, onActionSuccess, onSuccess, apiSource = 'transaction' } = properties;

  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [cdrData, setCdrData] = useState<any>();
  const [activeTab, setActiveTab] = useState<'details' | 'sessions' | 'order' | 'payments' | 'cdr'>(
    'details',
  );

  const currentSessionId =
    cdrData?.sessionId ||
    cdrData?.session_id ||
    cdrData?.cdrDetails?.sessionId ||
    cdrData?.cdrDetails?.session_id ||
    searchParameters.get('sessionId') ||
    undefined;
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<'release' | 'capture' | null>(null);

  // Insights state from the 2 real backend APIs
  const [chargeOrderData, setChargeOrderData] = useState<ChargeOrder | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentAttemptsResponse | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceInterface | null>(null);
  const [isInvoicePanelOpen, setIsInvoicePanelOpen] = useState<boolean>(false);
  const [invoiceActionLoading, setInvoiceActionLoading] = useState<'view' | 'export' | null>(null);
  const activeCdrIdReference = React.useRef<string | null>(null);

  const resolvedCdr = cdrData?.data || cdrData?.cdrDetails || cdrData;
  const paymentActions =
    resolvedCdr?.paymentActions ||
    resolvedCdr?.payment_actions ||
    cdrData?.paymentActions ||
    cdrData?.payment_actions ||
    cdrData?.payment?.paymentActions ||
    cdrData?.payment?.payment_actions ||
    cdrData?.cdrDetails?.paymentActions ||
    cdrData?.cdrDetails?.payment_actions;
  const hasPaymentActionPermission = permissionCheck('mobilityTransactionsPaymentAction');
  const canRelease =
    hasPaymentActionPermission &&
    (paymentActions?.canRelease === true ||
      paymentActions?.can_release === true ||
      String(paymentActions?.canRelease).toLowerCase() === 'true' ||
      String(paymentActions?.can_release).toLowerCase() === 'true');
  const canCapture =
    hasPaymentActionPermission &&
    (paymentActions?.canCapture === true ||
      paymentActions?.can_capture === true ||
      String(paymentActions?.canCapture).toLowerCase() === 'true' ||
      String(paymentActions?.can_capture).toLowerCase() === 'true');
  const captureReason =
    paymentActions?.reason ?? paymentActions?.reasons ?? paymentActions?.captureReason;
  const paymentFailedReason =
    cdrData?.payment?.paymentFailedReason ||
    cdrData?.payment?.payment_failed_reason ||
    cdrData?.paymentFailedReason ||
    cdrData?.payment_failed_reason ||
    paymentData?.summary?.failureReason;
  const paymentError =
    cdrData?.payment?.paymentError ||
    cdrData?.payment?.payment_error ||
    cdrData?.paymentError ||
    cdrData?.payment_error;
  const hasInvoice = Boolean(
    cdrData?.invoice?.invoiceNumber ||
      cdrData?.invoice?.invoiceId ||
      cdrData?.invoiceNumber ||
      cdrData?.invoiceId ||
      cdrData?.invoice?.isInvoiceGenerated ||
      cdrData?.isInvoiceGenerated ||
      selectedInvoice?.invoiceNumber ||
      selectedInvoice?._id ||
      selectedInvoice?.id,
  );

  /**
   * Helper function to get nested values from cdrData using dot notation.
   * @param {string} path - Dot-separated key path.
   * @param {boolean} date - Flag to convert to date format.
   * @param {boolean} [currency] - Flag to format as currency.
   * @param {boolean} [energy] - Flag to format as energy.
   * @returns {any} Value or fallback string.
   */
  const getNestedValue = (
    path: string,
    date: boolean,
    currency?: boolean,
    energy?: boolean,
  ): any => {
    let nestedValue: any = cdrData;
    try {
      const parts = path.split('.');
      parts.forEach((part) => {
        if (nestedValue !== null && nestedValue !== undefined) {
          const snakePart = part.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
          const possibleKeys = [part, snakePart];

          if (part === 'startDateTime' || part === 'startTime') {
            possibleKeys.push('startDateTime', 'startTime', 'start_date_time', 'start_time');
          }
          if (part === 'endDateTime' || part === 'endTime') {
            possibleKeys.push('endDateTime', 'endTime', 'end_date_time', 'end_time');
          }
          if (part === 'authId' || part === 'authReference' || part === 'authReferenceId') {
            possibleKeys.push(
              'authId',
              'auth_id',
              'authReference',
              'auth_reference',
              'authRef',
              'auth_ref',
            );
          }
          if (
            part === 'lastBillingAttempt' ||
            part === 'lastBillingAttemptAt' ||
            part === 'lastPaymentAttempt' ||
            part === 'lastPaymentAttemptAt'
          ) {
            possibleKeys.push(
              'lastBillingAttempt',
              'last_billing_attempt',
              'lastBillingAttemptAt',
              'last_billing_attempt_at',
              'lastPaymentAttempt',
              'last_payment_attempt',
              'lastPaymentAttemptAt',
              'last_payment_attempt_at',
            );
          }
          if (part === 'billingStatus' || part === 'paymentStatus') {
            possibleKeys.push('billingStatus', 'billing_status', 'paymentStatus', 'payment_status');
          }
          if (part === 'isBilled' || part === 'isPaid') {
            possibleKeys.push('isBilled', 'is_billed', 'isPaid', 'is_paid');
          }
          if (
            part === 'billingReason' ||
            part === 'paymentFailedReason' ||
            part === 'paymentReason'
          ) {
            possibleKeys.push(
              'billingReason',
              'billing_reason',
              'paymentFailedReason',
              'payment_failed_reason',
              'paymentReason',
              'payment_reason',
            );
          }
          if (part === 'billingError' || part === 'paymentError') {
            possibleKeys.push('billingError', 'billing_error', 'paymentError', 'payment_error');
          }
          if (part === 'billingAttempts' || part === 'paymentAttempts') {
            possibleKeys.push(
              'billingAttempts',
              'billing_attempts',
              'paymentAttempts',
              'payment_attempts',
            );
          }
          if (part === 'billedAt' || part === 'paidAt') {
            possibleKeys.push('billedAt', 'billed_at', 'paidAt', 'paid_at');
          }
          if (part === 'invoiceNumber') {
            possibleKeys.push('invoiceNumber', 'invoice_number');
          }
          if (part === 'invoiceId') {
            possibleKeys.push('invoiceId', 'invoice_id');
          }
          if (part === 'status') {
            possibleKeys.push('status', 'invoiceStatus', 'invoice_status');
          }
          if (part === 'statusLabel') {
            possibleKeys.push(
              'statusLabel',
              'status_label',
              'invoiceStatusLabel',
              'invoice_status_label',
            );
          }
          if (part === 'failedReason') {
            possibleKeys.push(
              'failedReason',
              'failed_reason',
              'failureReason',
              'failure_reason',
              'invoiceFailedReason',
              'invoice_failed_reason',
            );
          }
          if (part === 'lastAttemptAt') {
            possibleKeys.push(
              'lastAttemptAt',
              'last_attempt_at',
              'lastInvoiceAttemptAt',
              'last_invoice_attempt_at',
            );
          }
          if (part === 'gateway' || part === 'gatewayLabel') {
            possibleKeys.push('gatewayLabel', 'gateway_label', 'gateway');
          }

          const resolvedKey = possibleKeys.find(
            (key) => nestedValue[key] !== undefined && nestedValue[key] !== null,
          );
          nestedValue = resolvedKey !== undefined ? nestedValue[resolvedKey] : undefined;
        }
      });

      if (path === 'externalId' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue =
          cdrData?.externalId ||
          cdrData?.external_id ||
          cdrData?.cdrDetails?.id ||
          cdrData?.cdrId ||
          cdrData?.sessionId;
      }
      if (
        path === 'cdrDetails.userCustomId' &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.userCustomId ||
          cdrData?.cdrDetails?.userCustomId ||
          cdrData?.cdrDetails?.user_custom_id;
      }
      if (
        path === 'cdrDetails.totalEnergy' &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.totalEnergy ||
          cdrData?.total_energy ||
          cdrData?.energyKwh ||
          cdrData?.powerConsumedInKwh ||
          cdrData?.cdrDetails?.totalEnergy ||
          cdrData?.cdrDetails?.total_energy;
      }
      if (path === 'cdrDetails.totalCost' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue =
          cdrData?.totalCost ||
          cdrData?.total_cost ||
          cdrData?.totalAmount ||
          cdrData?.amount ||
          cdrData?.cdrDetails?.totalCost ||
          cdrData?.cdrDetails?.total_cost;
      }
      if (path === 'emspFees.finalAmount' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue =
          cdrData?.finalAmount ||
          cdrData?.final_amount ||
          cdrData?.emspFees?.finalAmount ||
          cdrData?.emspFees?.final_amount ||
          cdrData?.amount;
      }
      if (
        (path === 'payment.billingReason' ||
          path === 'payment.paymentReason' ||
          path === 'payment.paymentFailedReason') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.paymentFailedReason ||
          cdrData?.payment_failed_reason ||
          cdrData?.billingReason ||
          cdrData?.payment?.billingReason ||
          cdrData?.payment?.paymentFailedReason ||
          cdrData?.payment?.payment_failed_reason;
      }
      if (
        (path === 'payment.billingError' || path === 'payment.paymentError') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.paymentError ||
          cdrData?.billingError ||
          cdrData?.payment?.billingError ||
          cdrData?.payment?.paymentError;
      }
      if (
        (path === 'payment.billingAttempts' || path === 'payment.paymentAttempts') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.paymentAttempts ||
          cdrData?.billingAttempts ||
          cdrData?.payment?.billingAttempts ||
          cdrData?.payment?.paymentAttempts;
      }
      if (path === 'payment.paymentMethod' && (nestedValue === null || nestedValue === undefined)) {
        nestedValue = cdrData?.paymentMethod || cdrData?.payment?.paymentMethod;
      }
      if (
        path === 'payment.paymentIntentId' &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue = cdrData?.paymentIntentId || cdrData?.payment?.paymentIntentId;
      }
      if (
        path === 'payment.gatewayTransactionId' &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue = cdrData?.gatewayTransactionId || cdrData?.payment?.gatewayTransactionId;
      }
      if (
        (path === 'payment.gatewayLabel' || path === 'payment.gateway') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          paymentData?.summary?.gatewayLabel ||
          paymentData?.summary?.gateway ||
          cdrData?.payment?.gatewayLabel ||
          cdrData?.payment?.gateway ||
          cdrData?.gatewayLabel ||
          cdrData?.gateway;
      }
      if (
        (path === 'payment.lastBillingAttemptAt' || path === 'payment.lastPaymentAttemptAt') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.lastPaymentAttemptAt ||
          cdrData?.lastBillingAttemptAt ||
          cdrData?.payment?.lastBillingAttemptAt ||
          cdrData?.payment?.lastPaymentAttemptAt;
      }
      if (
        (path === 'payment.billedAt' || path === 'payment.paidAt') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.paidAt ||
          cdrData?.billedAt ||
          cdrData?.payment?.billedAt ||
          cdrData?.payment?.paidAt;
      }
      if (
        (path === 'invoice.isInvoiceGenerated' || path === 'invoice.is_invoice_generated') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        if (cdrData?.isInvoiceGenerated !== undefined) {
          nestedValue = cdrData.isInvoiceGenerated;
        } else if (cdrData?.invoice?.isInvoiceGenerated !== undefined) {
          nestedValue = cdrData.invoice.isInvoiceGenerated;
        } else if (cdrData?.invoice?.is_invoice_generated !== undefined) {
          nestedValue = cdrData?.invoice?.is_invoice_generated;
        } else if (selectedInvoice) {
          nestedValue = true;
        }
      }
      if (
        (path === 'invoice.invoiceNumber' || path === 'invoice.invoice_number') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.invoiceNumber ||
          cdrData?.invoice_number ||
          cdrData?.invoice?.invoiceNumber ||
          cdrData?.invoice?.invoice_number ||
          selectedInvoice?.invoiceNumber;
      }
      if (
        (path === 'invoice.invoiceId' || path === 'invoice.invoice_id') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.invoiceId ||
          cdrData?.invoice_id ||
          cdrData?.invoice?.invoiceId ||
          cdrData?.invoice?.invoice_id ||
          selectedInvoice?.id ||
          selectedInvoice?._id;
      }
      if (
        (path === 'invoice.status' || path === 'invoice.invoice_status') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.invoice?.status ??
          cdrData?.invoice?.invoice_status ??
          cdrData?.invoiceStatus ??
          cdrData?.invoice_status ??
          (selectedInvoice ? 'GENERATED' : undefined);
      }
      if (
        (path === 'invoice.statusLabel' || path === 'invoice.status_label') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.invoice?.statusLabel ??
          cdrData?.invoice?.status_label ??
          cdrData?.invoiceStatusLabel ??
          cdrData?.invoice_status_label ??
          (selectedInvoice ? 'Generated' : undefined);
      }
      if (
        (path === 'invoice.message' || path === 'invoice.invoice_message') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.invoice?.message ??
          cdrData?.invoice?.invoice_message ??
          cdrData?.invoiceMessage ??
          cdrData?.invoice_message;
      }
      if (
        (path === 'invoice.failedReason' || path === 'invoice.failed_reason') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.invoice?.failedReason ??
          cdrData?.invoice?.failed_reason ??
          cdrData?.invoiceFailedReason ??
          cdrData?.invoice_failed_reason;
      }
      if (
        (path === 'invoice.error' || path === 'invoice.invoice_error') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.invoice?.error ??
          cdrData?.invoice?.invoice_error ??
          cdrData?.invoiceError ??
          cdrData?.invoice_error;
      }
      if (
        (path === 'invoice.attempts' || path === 'invoice.invoice_attempts') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.invoice?.attempts ??
          cdrData?.invoice?.invoice_attempts ??
          cdrData?.invoiceAttempts ??
          cdrData?.invoice_attempts ??
          (selectedInvoice ? 1 : undefined);
      }
      if (
        (path === 'invoice.lastAttemptAt' || path === 'invoice.last_attempt_at') &&
        (nestedValue === null || nestedValue === undefined)
      ) {
        nestedValue =
          cdrData?.invoice?.lastAttemptAt ??
          cdrData?.invoice?.last_attempt_at ??
          cdrData?.invoiceLastAttemptAt ??
          cdrData?.invoice_last_attempt_at ??
          selectedInvoice?.createdAt ??
          selectedInvoice?.updatedAt;
      }

      if (nestedValue === null || nestedValue === undefined) {
        return '—';
      }

      if (typeof nestedValue === 'boolean') {
        return nestedValue ? t('yes') : t('no');
      }

      if (date) {
        return `${ConvertUTCtoDate(nestedValue)} - ${FormatUtcString(ConvertUTCtoHHMM(nestedValue))}`;
      }

      if (energy) {
        const energyValue = Number(nestedValue);
        if (!Number.isNaN(energyValue)) {
          if (energyValue >= 1000) {
            return `${ToFixedValue(energyValue / 1000, 3)} kWh`;
          }
          return `${ToFixedValue(energyValue, 3)} kWh`;
        }
      }

      if (currency) {
        const numberValue = Number(nestedValue);
        if (!Number.isNaN(numberValue)) {
          let currencyString = 'SAR';
          if (path.startsWith('emspFees.')) {
            currencyString =
              cdrData?.emspFees?.billingCurrency || cdrData?.billingCurrency || 'SAR';
          } else {
            currencyString = cdrData?.cdrDetails?.currency || cdrData?.currency || 'SAR';
          }
          return `${ToFixedValue(numberValue)} ${currencyString}`;
        }
      }
    } catch (error) {
      console.error(error);
    }
    return nestedValue !== undefined && nestedValue !== null ? String(nestedValue) : '—';
  };

  /**
   * Helper function to render header icons for each detail section.
   * @param {string} key - The icon key to match.
   * @returns {React.JSX.Element | null} The matching icon element or null.
   */
  const getSectionIcon = (key: string): React.JSX.Element | null => {
    switch (key) {
      case 'cdr-information':
        return <DocumentIcon />;
      case 'charging-order':
        return <LightningIcon />;
      case 'location':
        return <LocationIcon />;
      case 'energy-cost':
        return <LightningIcon />;
      case 'emsp-fees':
        return <CardIcon />;
      case 'payment':
        return <CardIcon />;
      case 'invoice':
        return <DocumentIcon />;
      case 'metadata':
        return <ClockIcon />;
      default:
        return null;
    }
  };

  /**
   * Resolves user-friendly capture reason text for disabled capture buttons.
   * @param {string} reasonCode - The raw reason code from backend.
   * @returns {string} The localized explanation message.
   */
  const getCaptureReasonHint = (reasonCode: string): string => {
    switch (reasonCode) {
      case 'no_hold':
        return t('noHoldHint');
      case 'hold_not_failed':
        return t('holdNotFailedHint');
      case 'retry_window_expired':
        return t('retryWindowExpiredHint');
      case 'already_paid':
      case 'ALREADY_CAPTURED':
      case 'already_captured':
        return t('alreadyCaptured');
      case 'ALREADY_RELEASED':
      case 'already_released':
        return t('alreadyReleased');
      case 'WAITING_FOR_AUTH':
      case 'waiting_for_auth':
        return t('waitingForAuth');
      case 'AUTH_PENDING':
      case 'auth_pending':
        return t('authPending');
      default:
        return reasonCode ? t('actionNotAllowed') : '';
    }
  };

  /**
   * Loads insights from active backend APIs (charge order, payment attempts, and invoice).
   * @param {string} sessionId - The session identifier.
   * @param {string} currentCdrId - The active CDR ID to prevent race conditions.
   * @param {string} [cdrId] - The CDR ID.
   * @param {string} [userCustomId] - The user custom ID.
   * @param {string} [authReference] - The authorization reference.
   * @param {string} [transactionId] - The transaction identifier.
   * @param {string} [invoiceId] - The invoice identifier.
   * @param {string} [invoiceNumber] - The invoice number.
   */
  const loadActiveApis = (
    sessionId: string,
    currentCdrId: string,
    cdrId?: string,
    userCustomId?: string,
    authReference?: string,
    transactionId?: string,
    invoiceId?: string,
    invoiceNumber?: string,
  ) => {
    if (!sessionId) return;

    // 1. Charge Order API: http://localhost:4007/emsp/api/v1/sessions/:sessionId/charge-order
    emspTransactionApi
      .fetchChargeOrder(sessionId)
      .then((orderResponse: any) => {
        if (activeCdrIdReference.current === currentCdrId) {
          const order = orderResponse?.data?.chargeOrder || orderResponse?.chargeOrder || null;
          setChargeOrderData(order);
        }
      })
      .catch(() => {
        if (activeCdrIdReference.current === currentCdrId) {
          setChargeOrderData(null);
        }
      });

    // 2. Payment Attempts API: http://localhost:4007/emsp/api/v1/sessions/:sessionId/payment-attempts
    const paymentQueryParameters: Record<string, any> = {};
    if (authReference) paymentQueryParameters.authReference = authReference;
    if (cdrId) paymentQueryParameters.cdrId = cdrId;
    if (userCustomId) paymentQueryParameters.userCustomId = userCustomId;

    emspTransactionApi
      .fetchPaymentAttempts(sessionId, paymentQueryParameters)
      .then((paymentResponse: any) => {
        if (activeCdrIdReference.current === currentCdrId) {
          let paymentResult = null;
          if (paymentResponse?.data?.summary || paymentResponse?.data?.timeline) {
            paymentResult = paymentResponse.data;
          } else if (paymentResponse?.summary || paymentResponse?.timeline) {
            paymentResult = paymentResponse;
          } else {
            paymentResult = paymentResponse?.data || paymentResponse || null;
          }
          setPaymentData(paymentResult);
        }
      })
      .catch(() => {
        if (activeCdrIdReference.current === currentCdrId) {
          setPaymentData(null);
          setSelectedInvoice(null);
          setIsInvoicePanelOpen(false);
          setInvoiceActionLoading(null);
        }
      });

    // 3. Invoice API: Fetch invoice automatically via searchBy (backend-supported query parameter)
    const searchTarget = invoiceNumber || invoiceId || sessionId || transactionId || cdrId;
    if (invoiceId) {
      invoiceApi
        .fetchInvoiceById(invoiceId)
        .then((idResponse: any) => {
          if (activeCdrIdReference.current === currentCdrId) {
            const invoiceObject = idResponse?.data?.data || idResponse?.data;
            if (
              invoiceObject &&
              (invoiceObject.invoiceNumber || invoiceObject._id || invoiceObject.id)
            ) {
              setSelectedInvoice(invoiceObject);
            }
          }
        })
        .catch(() => {
          if (searchTarget) {
            invoiceApi
              .fetchInvoiceList({ searchBy: searchTarget, limit: 10 })
              .then((invoiceResponse: any) => {
                if (activeCdrIdReference.current === currentCdrId) {
                  const list =
                    invoiceResponse?.data?.results ||
                    invoiceResponse?.results ||
                    (Array.isArray(invoiceResponse?.data) ? invoiceResponse.data : []);
                  if (Array.isArray(list) && list.length > 0) {
                    const matched = list.find(
                      (item: InvoiceInterface) =>
                        (invoiceNumber && item.invoiceNumber === invoiceNumber) ||
                        (invoiceId && (item.id === invoiceId || item._id === invoiceId)) ||
                        (sessionId && item.sessionId === sessionId) ||
                        (transactionId && item.transactionId === transactionId) ||
                        (cdrId && (item.id === cdrId || item._id === cdrId)),
                    );
                    if (matched) {
                      setSelectedInvoice(matched);
                    }
                  }
                }
              })
              .catch(() => {});
          }
        });
    } else if (searchTarget) {
      invoiceApi
        .fetchInvoiceList({ searchBy: searchTarget, limit: 10 })
        .then((invoiceResponse: any) => {
          if (activeCdrIdReference.current === currentCdrId) {
            const list =
              invoiceResponse?.data?.results ||
              invoiceResponse?.results ||
              (Array.isArray(invoiceResponse?.data) ? invoiceResponse.data : []);
            if (Array.isArray(list) && list.length > 0) {
              const matched = list.find(
                (item: InvoiceInterface) =>
                  (invoiceNumber && item.invoiceNumber === invoiceNumber) ||
                  (sessionId && item.sessionId === sessionId) ||
                  (transactionId && item.transactionId === transactionId) ||
                  (cdrId && (item.id === cdrId || item._id === cdrId)),
              );
              if (matched) {
                setSelectedInvoice(matched);
              }
            }
          }
        })
        .catch(() => {});
    }
  };

  /**
   * Function to fetch CDR details.
   * @param {string} cdrId - The CDR ID.
   * @returns {Promise<void>} Resolves when fetch completes.
   */
  const handleFetchCdrDetails = async (cdrId: string): Promise<void> => {
    const isSameLoadedCdr =
      Boolean(cdrData) &&
      (cdrData?._id === cdrId ||
        cdrData?.id === cdrId ||
        cdrData?.cdrId === cdrId ||
        cdrData?.externalId === cdrId ||
        cdrData?.cdrDetails?.id === cdrId);

    if (activeCdrIdReference.current !== cdrId && !isSameLoadedCdr) {
      setCdrData(null);
    }
    activeCdrIdReference.current = cdrId;
    setChargeOrderData(null);
    setPaymentData(null);
    setSelectedInvoice(null);
    setIsInvoicePanelOpen(false);
    setInvoiceActionLoading(null);

    try {
      let response: any;
      if (apiSource === 'commercialSession') {
        response = await emspSessionApi.fetchCommercialSessionById(cdrId);
        // Try enriching with CDR details by session ID if available
        try {
          const sessionCdr = await emspCdrApi.fetchEmspCdrDetailsBySessionId(cdrId);
          const cdrObject = sessionCdr?.data?.cdr || sessionCdr?.data;
          if (cdrObject && response?.data) {
            response.data = {
              ...response.data,
              ...cdrObject,
              cdrDetails: { ...(response.data?.cdrDetails || {}), ...(cdrObject?.cdrDetails || {}) },
              emspFees: { ...(response.data?.emspFees || {}), ...(cdrObject?.emspFees || {}) },
              payment: { ...(response.data?.payment || {}), ...(cdrObject?.payment || {}) },
              invoice: cdrObject?.invoice || response.data?.invoice,
            };
          }
        } catch {
          // Ignore if CDR details endpoint is not available for this session
        }
      } else {
        let targetCdrId = cdrId;
        if (!MONGO_OBJECT_ID_PATTERN.test(cdrId)) {
          const resolvedObjectId = await resolveCdrObjectId(cdrId);
          if (activeCdrIdReference.current !== cdrId) return;
          if (resolvedObjectId) {
            targetCdrId = resolvedObjectId;
          }
        }

        try {
          response = await emspCdrApi.fetchEmspCdrById(targetCdrId);
        } catch (fetchError: any) {
          const errorStatus =
            fetchError?.response?.status ?? fetchError?.response?.data?.code;
          if (errorStatus === 404 && targetCdrId === cdrId) {
            const fallbackObjectId = await resolveCdrObjectId(cdrId);
            if (activeCdrIdReference.current !== cdrId) return;
            if (fallbackObjectId && fallbackObjectId !== cdrId) {
              response = await emspCdrApi.fetchEmspCdrById(fallbackObjectId);
            } else {
              throw fetchError;
            }
          } else {
            throw fetchError;
          }
        }
      }
      if (activeCdrIdReference.current !== cdrId) return;

      const data = response?.data;
      setCdrData(data);

      const sessionId = data?.sessionId || data?.cdrDetails?.sessionId;
      const externalId = data?.externalId || data?.cdrId || data?.cdrDetails?.id;
      const customId =
        data?.userCustomId || data?.driver?.userCustomId || data?.cdrDetails?.userCustomId;
      const authReference =
        data?.authReference ||
        data?.cdrDetails?.authReference ||
        data?.cdrDetails?.auth_reference ||
        data?.payment?.authReference;
      const transactionId =
        data?.transactionId || data?.transaction_id || data?.cdrDetails?.transactionId;
      const invoiceId =
        data?.invoice?.invoiceId || data?.invoiceId || data?.invoice_id;
      const invoiceNumber =
        data?.invoice?.invoiceNumber || data?.invoiceNumber || data?.invoice_number;

      const effectiveSessionId =
        sessionId || (apiSource === 'commercialSession' ? cdrId : undefined);

      if (effectiveSessionId) {
        loadActiveApis(
          effectiveSessionId,
          cdrId,
          externalId,
          customId,
          authReference,
          transactionId,
          invoiceId,
          invoiceNumber,
        );
      }
    } catch (error: any) {
      if (activeCdrIdReference.current !== cdrId) return;
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || t('errorMessage'),
          type: 'error',
          code: 0,
        }),
      );
    }
  };

  /**
   * Unified error handler checking error codes per section 4 of the guide.
   * @param {any} error - The caught API error.
   */
  const handlePaymentActionError = (error: any) => {
    if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'warning',
          message: t('captureTimeout'),
          code: 408,
        }),
      );
      return;
    }

    const errorData = error?.response?.data;
    const errorCode = errorData?.errorCode;
    const status = error?.response?.status;

    if (status === 403 || errorCode === 'PERMISSION_DENIED') {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: t('permissionDenied'),
          code: 403,
        }),
      );
    } else if (errorCode === 'PAYMENT_ACTION_IN_PROGRESS') {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'warning',
          message: t('actionInProgress'),
          code: 409,
        }),
      );
    } else if (errorCode === 'PAYMENT_ACTION_NOT_ALLOWED') {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: errorData?.message || t('actionNotAllowed'),
          code: 400,
        }),
      );
    } else {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: errorData?.message || t('actionFailed'),
          code: status || 500,
        }),
      );
    }
  };

  /**
   * Executes payment hold release with outcome and error mapping.
   * @returns {Promise<void>} Resolves when the release action completes.
   */
  const handleExecuteRelease = async (): Promise<void> => {
    const cdrId = cdrData?.cdrId || cdrData?._id || cdrData?.id;
    if (!cdrId) return;

    setActionLoading(true);
    try {
      const response = await emspCdrApi.releasePaymentHold(cdrId);
      const outcome = response?.data?.outcome;
      const errorDetail = response?.data?.error;

      if (outcome === 'ALREADY_RELEASED') {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'info',
            message: t('alreadyReleasedMsg'),
            code: 200,
          }),
        );
      } else if (outcome === 'RELEASE_FAILED') {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message: errorDetail || t('releaseFailedMsg'),
            code: 0,
          }),
        );
      } else {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'success',
            message: t('releaseSuccess'),
            code: 200,
          }),
        );
      }

      setConfirmAction(null);
      const refreshTargetId =
        apiSource === 'commercialSession'
          ? activeCdrIdReference.current || cdrData?.sessionId || cdrId
          : cdrId;
      await handleFetchCdrDetails(refreshTargetId);
      onActionSuccess?.();
      onSuccess?.();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('emsp-payment-action-success', { detail: { cdrId, action: 'release' } }),
        );
      }
    } catch (error: any) {
      handlePaymentActionError(error);
      setConfirmAction(null);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Executes payment capture with outcome and error mapping.
   * @returns {Promise<void>} Resolves when the capture action completes.
   */
  const handleExecuteCapture = async (): Promise<void> => {
    const cdrId = cdrData?.cdrId || cdrData?._id || cdrData?.id;
    if (!cdrId) return;

    setActionLoading(true);
    try {
      const response = await emspCdrApi.capturePayment(cdrId);
      const outcome = response?.data?.outcome;
      const errorDetail = response?.data?.error;

      if (outcome === 'ALREADY_CAPTURED') {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'info',
            message: t('alreadyCapturedMsg'),
            code: 200,
          }),
        );
      } else if (outcome === 'CAPTURE_FAILED') {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message: errorDetail || t('captureFailedMsg'),
            code: 0,
          }),
        );
      } else {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'success',
            message: t('captureSuccess'),
            code: 200,
          }),
        );
      }

      setConfirmAction(null);
      const refreshTargetId =
        apiSource === 'commercialSession'
          ? activeCdrIdReference.current || cdrData?.sessionId || cdrId
          : cdrId;
      await handleFetchCdrDetails(refreshTargetId);
      onActionSuccess?.();
      onSuccess?.();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('emsp-payment-action-success', { detail: { cdrId, action: 'capture' } }),
        );
      }
    } catch (error: any) {
      handlePaymentActionError(error);
      setConfirmAction(null);
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    let resolvedCdrId: string | null = null;
    if (fetchIdFrom === 'queryParams') {
      resolvedCdrId =
        searchParameters.get('cdrObjectId') ||
        searchParameters.get('transactionObjectId') ||
        searchParameters.get('cdrId') ||
        searchParameters.get('id') ||
        searchParameters.get('sessionId');
    } else if (fetchIdFrom === 'props') {
      resolvedCdrId = id || null;
    }

    if (resolvedCdrId) {
      handleFetchCdrDetails(resolvedCdrId);
    }
    return () => {
      activeCdrIdReference.current = null;
    };
  }, [id, searchParameters, fetchIdFrom, apiSource]);

  /**
   * Constructs a fallback invoice interface from the active CDR data.
   * @returns {InvoiceInterface} Synthesized fallback invoice object.
   */
  const createFallbackInvoice = (): InvoiceInterface => {
    const invNumber =
      cdrData?.invoice?.invoiceNumber || cdrData?.invoiceNumber || cdrData?.invoice_number || '-';
    const invId = cdrData?.invoice?.invoiceId || cdrData?.invoiceId || cdrData?.invoice_id || '';
    const sessId =
      cdrData?.sessionId ||
      cdrData?.session_id ||
      cdrData?.cdrDetails?.sessionId ||
      currentSessionId ||
      '';
    const transId =
      cdrData?.transactionId || cdrData?.transaction_id || cdrData?.cdrDetails?.transactionId || '';
    const customId =
      cdrData?.userCustomId ||
      cdrData?.driver?.userCustomId ||
      cdrData?.cdrDetails?.userCustomId ||
      '';
    const currency =
      cdrData?.currency ||
      cdrData?.emspFees?.billingCurrency ||
      cdrData?.cdrDetails?.currency ||
      'SAR';
    const energyUsed = Number(
      cdrData?.totalEnergy ?? cdrData?.total_energy ?? cdrData?.cdrDetails?.totalEnergy ?? 0,
    );
    const amountPaid = Number(
      cdrData?.emspFees?.finalAmount ??
        cdrData?.totalCost ??
        cdrData?.total_cost ??
        cdrData?.cdrDetails?.totalCost ??
        0,
    );
    const startTime =
      cdrData?.startDateTime ||
      cdrData?.start_date_time ||
      cdrData?.cdrDetails?.startDateTime ||
      '';
    const stopTime =
      cdrData?.stopDateTime ||
      cdrData?.stop_date_time ||
      cdrData?.endDateTime ||
      cdrData?.end_date_time ||
      cdrData?.cdrDetails?.stopDateTime ||
      '';

    let durationString = '00:00:00';
    const rawDuration =
      cdrData?.totalTime ||
      cdrData?.total_time ||
      cdrData?.totalDuration ||
      cdrData?.cdrDetails?.totalTime;
    if (typeof rawDuration === 'string' && rawDuration.includes(':')) {
      durationString = rawDuration;
    } else if (Number(rawDuration) > 0) {
      const totalSec = Math.round(Number(rawDuration));
      const hours = Math.floor(totalSec / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;
      durationString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else if (startTime && stopTime) {
      const diffMs = Math.max(0, new Date(stopTime).getTime() - new Date(startTime).getTime());
      const totalSec = Math.floor(diffMs / 1000);
      const hours = Math.floor(totalSec / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;
      durationString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return {
      id: invId || invNumber,
      _id: invId,
      invoiceNumber: invNumber,
      invoiceDate: stopTime || startTime || cdrData?.createdAt || new Date().toISOString(),
      invoiceTime: '',
      financialYear: '',
      partyId: cdrData?.partyId || '',
      currency,
      invoiceFrom: {
        name: 'Energic',
        mobile: '',
        email: '',
        gstin: '',
        cin: '',
        billingAddress: '',
        sacCode: '',
      },
      invoiceTo: {
        name: cdrData?.driver?.name || cdrData?.userName || customId || '-',
        mobile: cdrData?.driver?.phone || cdrData?.userPhone || '',
        email: cdrData?.driver?.email || cdrData?.userEmail || '',
        pan: '',
        chargingLocation:
          cdrData?.location?.name || cdrData?.locationName || cdrData?.stationName || '',
      },
      chargerInfo: {
        stationId:
          cdrData?.stationId || cdrData?.location?.id || cdrData?.cdrDetails?.stationId || '',
        chargerId: cdrData?.chargerId || cdrData?.evseId || cdrData?.cdrDetails?.chargerId || '',
        connectorId: String(cdrData?.connectorId || cdrData?.cdrDetails?.connectorId || '1'),
        connectorType: cdrData?.connectorType || cdrData?.cdrDetails?.connectorType || '',
        connectorRating: cdrData?.connectorRating || cdrData?.cdrDetails?.connectorRating || '',
        accessType: cdrData?.accessType || '',
      },
      chargingDetails: {
        startTime,
        endTime: stopTime,
        chargingDate: startTime,
        energyUsed,
        energyOrdered: 0,
        chargingDuration: durationString,
        invoiceAmountPaid: amountPaid,
        costBreakdown: {
          consumptionCost: amountPaid,
          platformFee: Number(cdrData?.emspFees?.platformAmount || 0),
          sessionFee: Number(cdrData?.emspFees?.sessionAmount || 0),
          total: amountPaid,
          currency,
        },
        evVin: '',
        evNumber: '',
        bookingId: '',
      },
      sessionId: sessId,
      transactionId: transId,
      userCustomId: customId,
      createdAt: cdrData?.createdAt || '',
      updatedAt: cdrData?.updatedAt || '',
    };
  };

  /**
   * Fetches the complete invoice data for display or PDF generation.
   * @returns {Promise<InvoiceInterface | null>} The invoice data or null.
   */
  const fetchInvoiceData = async (): Promise<InvoiceInterface | null> => {
    if (selectedInvoice) {
      return selectedInvoice;
    }
    if (cdrData?.invoice?.chargingDetails && cdrData?.invoice?.invoiceTo) {
      return cdrData.invoice as InvoiceInterface;
    }

    const invId = cdrData?.invoice?.invoiceId || cdrData?.invoiceId || cdrData?.invoice_id;
    const invNumber =
      cdrData?.invoice?.invoiceNumber || cdrData?.invoiceNumber || cdrData?.invoice_number;
    const sessId =
      cdrData?.sessionId ||
      cdrData?.session_id ||
      cdrData?.cdrDetails?.sessionId ||
      currentSessionId;
    const transId =
      cdrData?.transactionId || cdrData?.transaction_id || cdrData?.cdrDetails?.transactionId;

    // 1. If invoiceId exists, try fetching by ID directly
    if (invId) {
      try {
        const idResponse = await invoiceApi.fetchInvoiceById(invId);
        const invoiceObject = idResponse?.data?.data || idResponse?.data;
        if (
          invoiceObject &&
          (invoiceObject.invoiceNumber || invoiceObject._id || invoiceObject.id)
        ) {
          return invoiceObject as InvoiceInterface;
        }
      } catch {
        // Fallback to search list
      }
    }

    // 2. Query invoice list using validated searchBy parameter (avoids invalid param 400 errors)
    const searchTarget = invNumber || invId || sessId || transId;
    if (searchTarget) {
      try {
        const response = await invoiceApi.fetchInvoiceList({ searchBy: searchTarget, limit: 10 });
        const list = response?.data?.results || response?.results;
        if (Array.isArray(list) && list.length > 0) {
          const matched = list.find(
            (item: InvoiceInterface) =>
              (invNumber && item.invoiceNumber === invNumber) ||
              (invId && (item.id === invId || item._id === invId)) ||
              (sessId && item.sessionId === sessId) ||
              (transId && item.transactionId === transId),
          );
          if (matched) {
            return matched;
          }
        }
      } catch {
        // Fallback to synthesizing invoice from CDR
      }
    }

    // 3. Fallback: synthesize invoice from CDR data so view and export ALWAYS succeed
    if (hasInvoice) {
      return createFallbackInvoice();
    }

    return null;
  };

  /**
   * Handles opening the invoice details side panel.
   * @returns {Promise<void>} Resolves when the view action completes.
   */
  const handleViewInvoice = async (): Promise<void> => {
    if (!hasInvoice) {
      return;
    }
    if (selectedInvoice) {
      setIsInvoicePanelOpen(true);
      return;
    }

    setInvoiceActionLoading('view');
    try {
      const invoice = await fetchInvoiceData();
      if (invoice) {
        setSelectedInvoice(invoice);
        setIsInvoicePanelOpen(true);
      } else {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'warning',
            message: t('invoiceNotFound', {
              defaultValue: 'Invoice not found for this transaction',
            }),
            code: 404,
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message:
            error?.response?.data?.message ||
            t('failedToFetchInvoice', { defaultValue: 'Failed to load invoice details' }),
          code: 500,
        }),
      );
    } finally {
      setInvoiceActionLoading(null);
    }
  };

  /**
   * Handles exporting the invoice as a PDF file.
   * @returns {Promise<void>} Resolves when the export finishes.
   */
  const handleExportInvoice = async (): Promise<void> => {
    if (!hasInvoice) {
      return;
    }
    setInvoiceActionLoading('export');
    try {
      const invoice = selectedInvoice || (await fetchInvoiceData());
      if (invoice) {
        if (!selectedInvoice) {
          setSelectedInvoice(invoice);
        }
        await downloadSingleInvoicePdf(invoice, i18n.language);
      } else {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'warning',
            message: t('invoiceNotFound', {
              defaultValue: 'Invoice not found for this transaction',
            }),
            code: 404,
          }),
        ); 
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message:
            error?.response?.data?.message ||
            t('failedToExportInvoice', { defaultValue: 'Failed to export invoice' }),
          code: 500,
        }),
      );
    } finally {
      setInvoiceActionLoading(null);
    }
  };

  /**
   * Renders the View and Export buttons in the invoice card header.
   * @returns {React.JSX.Element} The rendered header actions element.
   */
  const renderInvoiceHeaderActions = (): React.JSX.Element => {
    const isActionDisabled = invoiceActionLoading !== null || !hasInvoice;

    return (
      <div className="invoice-header-actions">
        <MyButton
          buttonType="primary-outline"
          label={t('view', { defaultValue: 'View' })}
          svgIcon={<EyeFillIcon />}
          loading={invoiceActionLoading === 'view'}
          disableButton={isActionDisabled}
          onClickFunc={handleViewInvoice}
        />
        <MyButton
          buttonType="primary-outline"
          label={t('export', { defaultValue: 'Export' })}
          svgIcon={<DocumentFileIcon />}
          loading={invoiceActionLoading === 'export'}
          disableButton={isActionDisabled}
          onClickFunc={handleExportInvoice}
        />
      </div>
    );
  };

  const cdrMap = [
    {
      header: t('energyCost'),
      iconKey: 'energy-cost',
      keyValuePair: [
        {
          label: t('totalEnergy'),
          key: 'cdrDetails.totalEnergy',
          energy: true,
        },
        {
          label: t('totalTime'),
          key: 'cdrDetails.totalTime',
          /**
           * Render total time custom details view.
           * @param {any} value - The input value object.
           * @returns {React.JSX.Element} The total time element.
           */
          view: (value: any) => {
            let displayTime = '—';
            const totalTime = value?.cdrDetails?.totalTime || value?.totalTime;
            if (totalTime !== undefined && totalTime !== null && !Number.isNaN(Number(totalTime))) {
              const totalSeconds = Math.round(Number(totalTime) * 3600);
              if (totalSeconds <= 0) {
                displayTime = '0s';
              } else {
                const days = Math.floor(totalSeconds / 86400);
                const hours = Math.floor((totalSeconds % 86400) / 3600);
                const minutes = Math.floor((totalSeconds % 3600) / 60);
                const seconds = totalSeconds % 60;

                if (days > 0) {
                  displayTime = `${days}d ${hours}h ${minutes}m ${seconds}s`;
                } else if (hours > 0) {
                  displayTime = `${hours}h ${minutes}m ${seconds}s`;
                } else if (minutes > 0) {
                  displayTime = `${minutes}m ${seconds}s`;
                } else {
                  displayTime = `${seconds}s`;
                }
              }
            }
            return (
              <div className="grid-item">
                <span className="item-label">{t('totalTime')}</span>
                <span className="item-value">{displayTime}</span>
              </div>
            );
          },
        },
        {
          label: t('totalCost'),
          key: 'cdrDetails.totalCost',
          currency: true,
        },
        {
          label: t('tariffID'),
          key: 'cdrDetails.tariffs',
          fullWidth: true,
          /**
           * Render the first tariff value with copy action.
           * @param {any} value - The input value object.
           * @returns {React.JSX.Element} The first tariff element.
           */
          view: (value: any) => {
            const list = value?.cdrDetails?.tariffs || value?.tariffs;
            const firstTariff = Array.isArray(list) ? list[0] : list;
            const tariffText =
              typeof firstTariff === 'string'
                ? firstTariff
                : firstTariff?.id || firstTariff?.tariffId || '—';

            return (
              <div className="grid-item full-width">
                <span className="item-label">{t('tariffID')}</span>
                <div className="item-value-wrap">
                  <span className="item-value">{tariffText}</span>
                  {tariffText && tariffText !== '—' && (
                    <Copy value={tariffText} size="small" hoverLabel={t('copyTariffID')} />
                  )}
                </div>
              </div>
            );
          },
        },
      ],
    },
    {
      header: t('cdrInformation'),
      iconKey: 'cdr-information',
      keyValuePair: [
        {
          label: t('externalId'),
          key: 'externalId',
          copy: true,
          fullWidth: true,
        },
        {
          label: t('roamingSessionId'),
          key: 'cdrDetails.sessionId',
          copy: true,
          fullWidth: true,
        },
        { label: t('partyId'), key: 'cdrDetails.partyId' },
        {
          label: t('countryCode'),
          key: 'cdrDetails.countryCode',
        },
        {
          label: t('userCustomId'),
          key: 'cdrDetails.userCustomId',
          copy: true,
        },
        { label: t('meterId'), key: 'cdrDetails.meterId' },
        {
          label: t('startTime'),
          key: 'cdrDetails.startTime',
          date: true,
        },
        {
          label: t('endTime'),
          key: 'cdrDetails.endTime',
          date: true,
        },
        {
          label: t('authenticationMethod'),
          key: 'cdrDetails.authMethod',
        },
        {
          label: t('authenticationReference'),
          key: 'cdrDetails.authReference',
        },
        { label: t('currency'), key: 'cdrDetails.currency' },
        { label: t('source'), key: 'cdrDetails.source' },
        {
          label: t('lastUpdated'),
          key: 'cdrDetails.lastUpdated',
          date: true,
        },
      ],
    },
    {
      header: t('chargingOrder'),
      iconKey: 'charging-order',
      keyValuePair: [
        {
          label: t('reference'),
          key: 'chargeOrder.reference',
          fullWidth: true,
          /**
           * Render order reference identifier spanning full row with copy action.
           * @returns {React.JSX.Element} Element.
           */
          view: () => {
            const referenceValue = chargeOrderData?.reference;
            return (
              <div className="grid-item full-width">
                <span className="item-label">{t('reference')}</span>
                <div className="item-value-wrap">
                  <span className="item-value">{referenceValue || '—'}</span>
                  {referenceValue && referenceValue !== '—' && (
                    <Copy
                      value={referenceValue}
                      size="small"
                      hoverLabel={t('copyOrderReference')}
                    />
                  )}
                </div>
              </div>
            );
          },
        },
        {
          label: t('chargingType'),
          key: 'chargeOrder.type',
          /**
           * Render charge order type.
           * @returns {React.JSX.Element} Element.
           */
          view: () => (
            <div className="grid-item">
              <span className="item-label">{t('chargingType')}</span>
              <span className="item-value">
                {chargeOrderData?.typeLabel || chargeOrderData?.type || '—'}
              </span>
            </div>
          ),
        },
        {
          label: t('orderQuantity'),
          key: 'chargeOrder.limit',
          /**
           * Render driver limit display.
           * @returns {React.JSX.Element} Element.
           */
          view: () => (
            <div className="grid-item">
              <span className="item-label">{t('orderQuantity')}</span>
              <span className="item-value">
                {chargeOrderData?.limit?.display ||
                  (chargeOrderData?.limit?.value
                    ? `${chargeOrderData.limit.value} ${chargeOrderData.limit.unit}`
                    : '—')}
              </span>
            </div>
          ),
        },
        {
          label: t('estimatedCost'),
          key: 'chargeOrder.estimate.cost',
          /**
           * Render estimated cost.
           * @returns {React.JSX.Element} Element.
           */
          view: () => (
            <div className="grid-item">
              <span className="item-label">{t('estimatedCost')}</span>
              <span className="item-value">
                {chargeOrderData?.estimate?.cost !== null &&
                chargeOrderData?.estimate?.cost !== undefined
                  ? `${chargeOrderData.estimate.cost} ${chargeOrderData.estimate.currency || 'SAR'}`
                  : '—'}
              </span>
            </div>
          ),
        },
        {
          label: t('estimatedEnergy'),
          key: 'chargeOrder.estimate.energyKwh',
          /**
           * Render estimated energy in kWh.
           * @returns {React.JSX.Element} Element.
           */
          view: () => (
            <div className="grid-item">
              <span className="item-label">{t('estimatedEnergy')}</span>
              <span className="item-value">
                {chargeOrderData?.estimate?.energyKwh !== null &&
                chargeOrderData?.estimate?.energyKwh !== undefined
                  ? `${chargeOrderData.estimate.energyKwh} kWh`
                  : '—'}
              </span>
            </div>
          ),
        },
        {
          label: t('stoppedAutomaticallyAt'),
          key: 'chargeOrder.limitReachedAt',
          /**
           * Render timestamp when order automatically ended.
           * @returns {React.JSX.Element} Element.
           */
          view: () => {
            const stoppedAt =
              chargeOrderData?.limitReachedAt ?? chargeOrderData?.stoppedAutomaticallyAt;
            const displayDate = stoppedAt
              ? `${ConvertUTCtoDate(stoppedAt)} - ${FormatUtcString(ConvertUTCtoHHMM(stoppedAt))}`
              : '—';
            return (
              <div className="grid-item">
                <span className="item-label">{t('stoppedAutomaticallyAt')}</span>
                <span className="item-value">{displayDate}</span>
              </div>
            );
          },
        },
      ],
    },
    {
      header: t('location'),
      iconKey: 'location',
      keyValuePair: [
        {
          label: t('locationId'),
          key: 'cdrDetails.location.id',
          copy: true,
          fullWidth: true,
        },
        {
          label: t('stationName'),
          key: 'cdrDetails.location.name',
        },
        { label: t('address'), key: 'cdrDetails.location.address' },
        { label: t('city'), key: 'cdrDetails.location.city' },
        {
          label: t('postalCode'),
          key: 'cdrDetails.location.postalCode',
        },
        { label: t('country'), key: 'cdrDetails.location.country' },
        {
          label: t('latitude'),
          key: 'cdrDetails.location.coordinates.latitude',
        },
        {
          label: t('longitude'),
          key: 'cdrDetails.location.coordinates.longitude',
        },
        {
          label: t('evseUid'),
          key: 'cdrDetails.location.evseUid',
          copy: true,
          fullWidth: true,
        },
        { label: t('evseId'), key: 'cdrDetails.location.evseId' },
        {
          label: t('connectorId'),
          key: 'cdrDetails.location.connectorId',
        },
        {
          label: t('connectorStandard'),
          key: 'cdrDetails.location.connectorStandard',
        },
        {
          label: t('connectorFormat'),
          key: 'cdrDetails.location.connectorFormat',
        },
        {
          label: t('connectorPowerType'),
          key: 'cdrDetails.location.connectorPowerType',
        },
      ],
    },
    {
      header: t('mobilityFees'),
      iconKey: 'emsp-fees',
      keyValuePair: [
        {
          label: t('networkCDRAmount'),
          key: 'cdrDetails.totalCost',
          currency: true,
        },
        {
          label: t('cdrAmountInBillingCurrency'),
          key: 'emspFees.cdrTotalCost',
          /**
           * Render cdr total cost custom details view.
           * @param {any} value - The input value object.
           * @returns {React.JSX.Element} The cdr total cost element.
           */
          view: (value: any) => {
            const cost = value?.emspFees?.cdrTotalCost ?? value?.cdrTotalCost;
            if (cost === undefined || cost === null || cost === '—') {
              return (
                <div className="grid-item">
                  <span className="item-label">{t('cdrAmountInBillingCurrency')}</span>
                  <span className="item-value">—</span>
                </div>
              );
            }
            const currencyString =
              value?.emspFees?.billingCurrency || value?.billingCurrency || 'SAR';
            const formattedValue = `${ToFixedValue(Number(cost))} ${currencyString}`;
            return (
              <div className="grid-item">
                <span className="item-label">{t('cdrAmountInBillingCurrency')}</span>
                <span className="item-value">{formattedValue}</span>
              </div>
            );
          },
        },
        {
          label: t('fxRate'),
          key: 'emspFees.fxRate',
          /**
           * Render FX rate custom details view if available.
           * @param {any} value - The input value object.
           * @returns {React.JSX.Element | null} The FX rate element or null.
           */
          view: (value: any) => {
            const fxRate =
              value?.emspFees?.fxRate !== undefined && value?.emspFees?.fxRate !== null
                ? value?.emspFees?.fxRate
                : value?.fxRate;
            if (fxRate === undefined || fxRate === null || fxRate === '' || fxRate === '—') {
              return null;
            }
            return (
              <div className="grid-item">
                <span className="item-label">{t('fxRate')}</span>
                <span className="item-value">{String(fxRate)}</span>
              </div>
            );
          },
        },
        {
          label: t('fxRateTimestamp'),
          key: 'emspFees.fxRateTimestamp',
          /**
           * Render FX rate timestamp custom details view if available.
           * @param {any} value - The input value object.
           * @returns {React.JSX.Element | null} The FX rate timestamp element or null.
           */
          view: (value: any) => {
            const fxRateTimestamp =
              value?.emspFees?.fxRateTimestamp ||
              value?.emspFees?.fx_rate_timestamp ||
              value?.fxRateTimestamp ||
              value?.fx_rate_timestamp;
            if (!fxRateTimestamp || fxRateTimestamp === '—') {
              return null;
            }
            const formattedDate = `${ConvertUTCtoDate(fxRateTimestamp)} - ${FormatUtcString(
              ConvertUTCtoHHMM(fxRateTimestamp),
            )}`;
            return (
              <div className="grid-item">
                <span className="item-label">{t('fxRateTimestamp')}</span>
                <span className="item-value">{formattedDate}</span>
              </div>
            );
          },
        },
        {
          label: t('platformAmount'),
          key: 'emspFees.platformFee',
          /**
           * Render platform fee custom details view based on fee type.
           * @param {any} value - The input value object.
           * @returns {React.JSX.Element} The platform fee element.
           */
          view: (value: any) => {
            const rawAmount =
              value?.emspFees?.platformAmount !== undefined
                ? value?.emspFees?.platformAmount
                : value?.platformAmount;
            const fee =
              value?.emspFees?.platformFee !== undefined
                ? value?.emspFees?.platformFee
                : value?.platformFee;
            const feeType =
              value?.emspFees?.platformFeeType !== undefined
                ? value?.emspFees?.platformFeeType
                : value?.platformFeeType;

            const isPercentage = String(feeType).toLowerCase() === 'percentage';

            let resolvedAmount =
              rawAmount !== undefined && rawAmount !== null && rawAmount !== '' && rawAmount !== '—'
                ? rawAmount
                : undefined;

            if (resolvedAmount === undefined && !isPercentage) {
              resolvedAmount = fee;
            }

            const hasValidAmount =
              resolvedAmount !== undefined &&
              resolvedAmount !== null &&
              resolvedAmount !== '' &&
              resolvedAmount !== '—' &&
              !Number.isNaN(Number(resolvedAmount));

            const hasValidFee =
              fee !== undefined &&
              fee !== null &&
              fee !== '' &&
              fee !== '—' &&
              !Number.isNaN(Number(fee));

            if (!hasValidAmount && !hasValidFee) {
              return (
                <div className="grid-item">
                  <span className="item-label">{t('platformFee')}</span>
                  <span className="item-value">—</span>
                </div>
              );
            }

            const currencyString =
              value?.emspFees?.billingCurrency || value?.billingCurrency || 'SAR';

            const amountDisplay = hasValidAmount
              ? `${ToFixedValue(Number(resolvedAmount), 2)} ${currencyString}`
              : '';

            const typeSuffix = isPercentage ? '(%)' : `(${t('fixed')})`;

            let formattedValue = '—';
            if (amountDisplay) {
              formattedValue = `${amountDisplay} ${typeSuffix}`;
            } else if (hasValidFee) {
              formattedValue = isPercentage
                ? `${fee}%`
                : `${ToFixedValue(Number(fee), 2)} ${currencyString} (${t('fixed')})`;
            }

            return (
              <div className="grid-item">
                <span className="item-label">{t('platformFee')}</span>
                <span className="item-value">{formattedValue}</span>
              </div>
            );
          },
        },
        {
          label: t('paymentSurcharge'),
          key: 'emspFees.sessionAmount',
          /**
           * Render surcharge amount with currency and surcharge fee percentage.
           * @param {any} value - The input value object.
           * @returns {React.JSX.Element} The combined surcharge amount element.
           */
          view: (value: any) => {
            const amount =
              value?.emspFees?.sessionAmount !== undefined
                ? value?.emspFees?.sessionAmount
                : value?.sessionAmount;
            const fee =
              value?.emspFees?.sessionFee !== undefined
                ? value?.emspFees?.sessionFee
                : value?.sessionFee;
            const feeType =
              value?.emspFees?.sessionFeeType !== undefined
                ? value?.emspFees?.sessionFeeType
                : value?.sessionFeeType;

            const currencyString =
              value?.emspFees?.billingCurrency || value?.billingCurrency || 'SAR';

            const amountNumber = Number(amount);
            const feeNumber = Number(fee);

            const hasValidAmount =
              amount !== undefined &&
              amount !== null &&
              amount !== '' &&
              amount !== '—' &&
              !Number.isNaN(amountNumber);

            const hasValidFee =
              fee !== undefined &&
              fee !== null &&
              fee !== '' &&
              fee !== '—' &&
              !Number.isNaN(feeNumber);

            if (!hasValidAmount && !hasValidFee) {
              return (
                <div className="grid-item">
                  <span className="item-label">{t('paymentSurcharge')}</span>
                  <span className="item-value">—</span>
                </div>
              );
            }

            const amountDisplay = hasValidAmount
              ? `${ToFixedValue(amountNumber, 2)} ${currencyString}`
              : '';

            let feeDisplay = '';
            if (hasValidFee) {
              const formattedFee = ToFixedValue(feeNumber, 2);
              if (
                String(feeType).toLowerCase() === 'percentage' ||
                feeType === undefined ||
                feeType === null
              ) {
                feeDisplay = `(${formattedFee}%)`;
              } else {
                feeDisplay = `(${formattedFee} ${currencyString})`;
              }
            }

            let combinedDisplay = amountDisplay;
            if (amountDisplay && feeDisplay) {
              combinedDisplay = `${amountDisplay} ${feeDisplay}`;
            } else if (feeDisplay) {
              combinedDisplay = feeDisplay;
            }

            return (
              <div className="grid-item">
                <span className="item-label">{t('paymentSurcharge')}</span>
                <span className="item-value">{combinedDisplay}</span>
              </div>
            );
          },
        },
        {
          label: t('finalAmount'),
          key: 'emspFees.finalAmount',
          currency: true,
        },
      ],
    },
    {
      header: t('payment'),
      iconKey: 'payment',
      keyValuePair: [
        {
          label: t('paymentStatus'),
          key: 'payment.paymentStatus',
          /**
           * Render payment status.
           * @returns {React.JSX.Element} Element.
           */
          view: () => {
            const status =
              paymentData?.summary?.statusLabel ||
              cdrData?.payment?.paymentStatus ||
              cdrData?.payment?.billingStatus ||
              '—';
            let statusClassName = '';
            const upperStatus = String(status).toUpperCase();
            if (upperStatus === 'SUCCESS' || upperStatus === 'PAID') {
              statusClassName = 'status-success';
            } else if (upperStatus === 'FAILED' || upperStatus === 'UNPAID') {
              statusClassName = 'status-failed';
            } else if (upperStatus === 'PENDING') {
              statusClassName = 'status-pending';
            }
            return (
              <div className="grid-item">
                <span className="item-label">{t('paymentStatus')}</span>
                <span className={`item-value ${statusClassName}`}>{status}</span>
              </div>
            );
          },
        },
        {
          label: t('gatewayLabel'),
          key: 'payment.gatewayLabel',
          /**
           * Render payment gateway.
           * @returns {React.JSX.Element} Element.
           */
          view: () => {
            const gateway =
              paymentData?.summary?.gatewayLabel ||
              paymentData?.summary?.gateway ||
              cdrData?.payment?.gatewayLabel ||
              cdrData?.payment?.gateway ||
              cdrData?.gateway ||
              '—';
            return (
              <div className="grid-item">
                <span className="item-label">{t('gatewayLabel')}</span>
                <span className="item-value">{gateway}</span>
              </div>
            );
          },
        },
        {
          label: t('paymentMethod'),
          key: 'payment.paymentMethod',
          /**
           * Render payment method.
           * @returns {React.JSX.Element} Element.
           */
          view: () => {
            const method =
              cdrData?.payment?.paymentMethod || paymentData?.summary?.paymentMethod || '—';
            return (
              <div className="grid-item">
                <span className="item-label">{t('paymentMethod')}</span>
                <span className="item-value capitalize">{method}</span>
              </div>
            );
          },
        },
      ],
    },
    {
      header: t('invoiceInformation'),
      iconKey: 'invoice',
      keyValuePair: [
        {
          label: t('isInvoiceGenerated'),
          key: 'invoice.isInvoiceGenerated',
        },
        {
          label: t('invoiceStatus'),
          key: 'invoice.status',
        },
        {
          label: t('invoiceStatusLabel'),
          key: 'invoice.statusLabel',
        },
        {
          label: t('invoiceNumber'),
          key: 'invoice.invoiceNumber',
          copy: true,
        },
        {
          label: t('invoiceId'),
          key: 'invoice.invoiceId',
          copy: true,
        },
        {
          label: t('attempts'),
          key: 'invoice.attempts',
        },
        {
          label: t('lastAttemptAt'),
          key: 'invoice.lastAttemptAt',
          date: true,
        },
        {
          label: t('failedReason'),
          key: 'invoice.failedReason',
        },
        {
          label: t('error'),
          key: 'invoice.error',
        },
      ],
    },
    {
      header: t('timestamp'),
      iconKey: 'metadata',
      keyValuePair: [
        { label: t('createdAt'), key: 'createdAt', date: true },
        { label: t('updatedAt'), key: 'updatedAt', date: true },
      ],
    },
  ];

  const activeSections = cdrMap.filter((section) => {
    if (activeTab === 'details') {
      return section.iconKey === 'emsp-fees' || section.iconKey === 'metadata';
    }
    if (activeTab === 'sessions') {
      return false;
    }
    if (activeTab === 'order') {
      return section.iconKey === 'charging-order';
    }
    if (activeTab === 'payments') {
      return section.iconKey === 'payment' || section.iconKey === 'invoice';
    }
    return (
      section.iconKey === 'energy-cost' ||
      section.iconKey === 'cdr-information' ||
      section.iconKey === 'location'
    );
  });

  /**
   * Renders the top summary highlight card displaying status, energy, CDR convert amount, and final amount.
   * @returns {React.JSX.Element} The rendered summary card element.
   */
  const renderSummaryCard = (): React.JSX.Element => {
    const rawPaymentStatus =
      paymentData?.summary?.statusLabel ||
      cdrData?.payment?.paymentStatus ||
      cdrData?.payment?.billingStatus ||
      (cdrData?.payment?.isPaid ? 'PAID' : undefined) ||
      '—';

    const normalizedStatus = String(rawPaymentStatus).toUpperCase();
    let summaryStatusText = rawPaymentStatus;
    let summaryStatusClass = 'status-pending';

    if (normalizedStatus === 'SUCCESS' || normalizedStatus === 'PAID') {
      summaryStatusText = t('paid');
      summaryStatusClass = 'status-paid';
    } else if (normalizedStatus === 'FAILED' || normalizedStatus === 'UNPAID') {
      summaryStatusText = t('failed');
      summaryStatusClass = 'status-failed';
    } else if (normalizedStatus === 'PENDING') {
      summaryStatusText = t('pending');
      summaryStatusClass = 'status-pending';
    }

    const rawTotalEnergy =
      cdrData?.totalEnergy ??
      cdrData?.total_energy ??
      cdrData?.cdrDetails?.totalEnergy ??
      cdrData?.cdrDetails?.total_energy;
    const energyNumber = Number(rawTotalEnergy);
    let summaryEnergyValue = '0.000';
    if (rawTotalEnergy !== null && rawTotalEnergy !== undefined && !Number.isNaN(energyNumber)) {
      summaryEnergyValue =
        energyNumber >= 1000 ? ToFixedValue(energyNumber / 1000, 3) : ToFixedValue(energyNumber, 3);
    }

    const rawCdrCost =
      cdrData?.totalCost ??
      cdrData?.total_cost ??
      cdrData?.cdrDetails?.totalCost ??
      cdrData?.cdrDetails?.total_cost;
    const cdrCostNumber = Number(rawCdrCost);
    const summaryCdrCostValue =
      rawCdrCost !== null && rawCdrCost !== undefined && !Number.isNaN(cdrCostNumber)
        ? ToFixedValue(cdrCostNumber, 2)
        : '0.00';
    const summaryCdrCurrency = cdrData?.cdrDetails?.currency || cdrData?.currency || 'SAR';

    const rawFinalAmount =
      cdrData?.finalAmount ??
      cdrData?.final_amount ??
      cdrData?.emspFees?.finalAmount ??
      cdrData?.emspFees?.final_amount ??
      cdrData?.amount;
    const finalAmountNumber = Number(rawFinalAmount);
    const summaryFinalAmountValue =
      rawFinalAmount !== null && rawFinalAmount !== undefined && !Number.isNaN(finalAmountNumber)
        ? ToFixedValue(finalAmountNumber, 2)
        : '0.00';
    const summaryFinalCurrency =
      cdrData?.emspFees?.billingCurrency || cdrData?.billingCurrency || summaryCdrCurrency;

    return (
      <div className="summary-highlight-container">
        <div className="summary-highlight-card">
          <div className="summary-grid">
            {/* 2. Total Energy */}
            <div className="summary-col">
              <span className="summary-col-label">{t('totalEnergy')}</span>
              <div className="summary-value-wrap">
                <span className="summary-value-num">{summaryEnergyValue}</span>
                <span className="summary-value-unit">kWh</span>
              </div>
            </div>

            {/* 3. CDR Convert Amount */}
            <div className="summary-col">
              <span className="summary-col-label">{t('cdrAmount')}</span>
              <div className="summary-value-wrap">
                <span className="summary-value-num">{summaryCdrCostValue}</span>
                <span className="summary-value-unit">{summaryCdrCurrency}</span>
              </div>
            </div>

            {/* 4. Final Amount */}
            <div className="summary-col final-amount-col">
              <span className="summary-col-label highlight-label">{t('finalAmount')}</span>
              <div className="summary-value-wrap">
                <span className="summary-value-num highlight-num">{summaryFinalAmountValue}</span>
                <span className="summary-value-unit highlight-unit">{summaryFinalCurrency}</span>
              </div>
            </div>

            {/* 1. Payment Status */}
            <div className="summary-col status-col">
              <span className="summary-col-label">{t('paymentStatus')}</span>
              <div className={`status-pill ${summaryStatusClass}`}>
                <span className="status-dot" aria-hidden="true" />
                <span className="status-text">{summaryStatusText}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render session tab content based on session ID and CDR data availability.
   * @returns {React.JSX.Element} Rendered session panel or empty state.
   */
  const renderSessionsTabContent = (): React.JSX.Element => {
    if (currentSessionId) {
      return (
        <EmspSessionSidePanel key={currentSessionId} fetchIdFrom="props" id={currentSessionId} />
      );
    }

    if (cdrData) {
      return <NoDataAvailable title={t('noDataFound')} description={t('noDataDescription')} />;
    }

    return <EmspSessionSidePanel fetchIdFrom="props" id={currentSessionId} />;
  };

  return (
    <div className="emsp-cdr-side-panel-wrap">
      {/* Scrollable Middle Content */}
      <div className="panel-scrollable-content">
        {/* Top Summary Highlight Card */}
        {renderSummaryCard()}

        {/* Tab Navigation */}
        <TabToggle
          tabs={[
            {
              label: t('detailsTransaction'),
              value: 'details',
            },
            {
              label: t('session'),
              value: 'sessions',
            },
            {
              label: t('order'),
              value: 'order',
            },
            {
              label: t('payments'),
              value: 'payments',
            },
            {
              label: t('networkCDR'),
              value: 'cdr',
            },
          ]}
          activeTab={activeTab}
          defaultTab="details"
          isLabelValue
          onChange={(value) =>
            setActiveTab(value as 'details' | 'sessions' | 'order' | 'payments' | 'cdr')
          }
        />

        {/* Sessions Tab Content: Render EmspSessionSidePanel or NoDataAvailable */}
        {activeTab === 'sessions'
          ? renderSessionsTabContent()
          : activeSections.map((section, index) => (
              <div className="detail-section-card" key={index}>
                <div className={`card-header ${section.iconKey}`}>
                  <div className="card-header-left">
                    {getSectionIcon(section.iconKey)}
                    <h3>{section.header}</h3>
                  </div>
                  {section.iconKey === 'invoice' && renderInvoiceHeaderActions()}
                </div>

                {/* Narrative message banner for payment card if present */}
                {section.iconKey === 'payment' && paymentData?.summary?.message && (
                  <div className="payment-narrative-box">
                    <p>{paymentData.summary.message}</p>
                  </div>
                )}

                {/* Narrative message banner for invoice card if present */}
                {section.iconKey === 'invoice' &&
                  Boolean(cdrData?.invoice?.message || cdrData?.invoiceMessage) && (
                    <div className="payment-narrative-box">
                      <p>{cdrData?.invoice?.message || cdrData?.invoiceMessage}</p>
                    </div>
                  )}

                <div className="card-body-grid">
                  {section.keyValuePair.map((item: any, itemIndex: number) => {
                    if (item.view) {
                      return <React.Fragment key={itemIndex}>{item.view(cdrData)}</React.Fragment>;
                    }
                    const nestedValue = getNestedValue(
                      item.key,
                      item.date || false,
                      item.currency || false,
                      item.energy || false,
                    );

                    const isPaymentStatusKey =
                      item.key === 'payment.billingStatus' || item.key === 'payment.paymentStatus';
                    const isInvoiceStatusKey = item.key === 'invoice.status';

                    let displayValue = nestedValue;
                    let statusClassName = '';

                    if (isPaymentStatusKey && typeof nestedValue === 'string') {
                      const upperStatus = nestedValue.toUpperCase();
                      if (upperStatus === 'SUCCESS' || upperStatus === 'PAID') {
                        statusClassName = 'status-success';
                        displayValue = t('paid');
                      } else if (upperStatus === 'FAILED' || upperStatus === 'UNPAID') {
                        statusClassName = 'status-failed';
                        displayValue = t('failed');
                      } else if (upperStatus === 'PENDING') {
                        statusClassName = 'status-pending';
                        displayValue = t('pending');
                      }
                    }

                    if (
                      isInvoiceStatusKey &&
                      typeof nestedValue === 'string' &&
                      nestedValue !== '—'
                    ) {
                      const upperStatus = nestedValue.toUpperCase();
                      if (
                        upperStatus === 'SUCCESS' ||
                        upperStatus === 'PAID' ||
                        upperStatus === 'GENERATED' ||
                        upperStatus === 'ISSUED'
                      ) {
                        statusClassName = 'status-success';
                      } else if (upperStatus === 'FAILED' || upperStatus === 'ERROR') {
                        statusClassName = 'status-failed';
                      } else if (
                        upperStatus === 'PENDING' ||
                        upperStatus === 'NOT_REQUESTED' ||
                        upperStatus === 'REQUESTED' ||
                        upperStatus === 'PROCESSING'
                      ) {
                        statusClassName = 'status-pending';
                      }
                    }

                    const canCopy = Boolean(item.copy && nestedValue && nestedValue !== '—');
                    const isFullWidth = Boolean(item.fullWidth);

                    return (
                      <div
                        className={`grid-item${isFullWidth ? ' full-width' : ''}`}
                        key={itemIndex}>
                        <span className="item-label">{item.label}</span>
                        {canCopy ? (
                          <div className="item-value-wrap">
                            <span className={`item-value ${statusClassName}`}>{displayValue}</span>
                            <Copy
                              value={nestedValue}
                              size="small"
                              hoverLabel={t('copyValue', { label: item.label })}
                            />
                          </div>
                        ) : (
                          <span className={`item-value ${statusClassName}`}>{displayValue}</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Payment attempts lifecycle timeline if available */}
                {section.iconKey === 'payment' &&
                  paymentData?.timeline &&
                  paymentData.timeline.length > 0 && (
                    <div className="payment-timeline-wrap">
                      <span className="timeline-title">{t('paymentTimeline')}</span>
                      {paymentData.timeline.map(
                        (stepItem: PaymentTimelineItem, stepIndex: number) => {
                          let dotClass = 'dot-pending';
                          if (stepItem.status === 'SUCCESS') {
                            dotClass = 'dot-success';
                          } else if (stepItem.status === 'FAILED') {
                            dotClass = 'dot-failed';
                          }

                          return (
                            <div
                              className="timeline-item"
                              key={`${stepItem.step}-${stepItem.at || stepIndex}`}>
                              <div className="timeline-rail">
                                <span className={`timeline-dot ${dotClass}`} />
                                {stepIndex < paymentData.timeline.length - 1 && (
                                  <span className="timeline-connector" />
                                )}
                              </div>
                              <div className="timeline-body">
                                <div className="step-header">
                                  <span>{stepItem.label}</span>
                                  <span className="step-amount">
                                    · {ToFixedValue(stepItem.amount, 2)} {stepItem.currency}
                                  </span>
                                  <span className="step-time">
                                    · {ConvertUTCtoDateTime(stepItem.at)}
                                  </span>
                                </div>
                                <p className="step-desc">{stepItem.description}</p>
                                {stepItem.error && <p className="step-error">{stepItem.error}</p>}
                                {Boolean(
                                  stepItem.gatewayTransactionId ||
                                    (stepItem as any).gateway_transaction_id,
                                ) && (
                                  <div className="step-meta">
                                    <span className="meta-label">{t('gatewayTransactionId')}:</span>{' '}
                                    <span className="meta-value">
                                      {stepItem.gatewayTransactionId ||
                                        (stepItem as any).gateway_transaction_id}
                                    </span>
                                    <Copy
                                      value={
                                        stepItem.gatewayTransactionId ||
                                        (stepItem as any).gateway_transaction_id
                                      }
                                      size="small"
                                      hoverLabel={t('copyGatewayTransactionId')}
                                    />
                                  </div>
                                )}
                                {Boolean(stepItem.preAuthId || (stepItem as any).pre_auth_id) && (
                                  <div className="step-meta">
                                    <span className="meta-label">{t('gatewayPreAuthId')}:</span>{' '}
                                    <span className="meta-value">
                                      {stepItem.preAuthId || (stepItem as any).pre_auth_id}
                                    </span>
                                    <Copy
                                      value={stepItem.preAuthId || (stepItem as any).pre_auth_id}
                                      size="small"
                                      hoverLabel={t('copyGatewayPreAuthId')}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}

                {/* Payment Actions Container */}
                {section.iconKey === 'payment' &&
                  (canRelease ||
                    canCapture ||
                    Boolean(hasPaymentActionPermission && captureReason && !canCapture) ||
                    Boolean(paymentFailedReason || paymentError)) && (
                    <div className="cdr-payment-actions-container">
                      <div className="payment-actions-header">
                        <div className="payment-actions-title-wrap">
                          <h4 className="actions-title">{t('paymentActions')}</h4>
                          {paymentFailedReason && (
                            <span className="payment-failed-reason-badge">
                              {paymentFailedReason}
                            </span>
                          )}
                          {paymentError && (
                            <span className="payment-error-text">{paymentError}</span>
                          )}
                        </div>
                        {captureReason &&
                          !canCapture &&
                          Boolean(getCaptureReasonHint(captureReason)) && (
                            <span className="payment-reason-hint">
                              {getCaptureReasonHint(captureReason)}
                            </span>
                          )}
                      </div>

                      <div className="payment-actions-buttons">
                        {canRelease && (
                          <MyButton
                            label={t('release')}
                            buttonType="error"
                            disableButton={actionLoading || confirmAction !== null}
                            loading={actionLoading && confirmAction === 'release'}
                            onClickFunc={() => setConfirmAction('release')}
                          />
                        )}

                        {canCapture && (
                          <MyButton
                            label={t('capture')}
                            buttonType="primary"
                            disableButton={actionLoading || confirmAction !== null}
                            loading={actionLoading && confirmAction === 'capture'}
                            onClickFunc={() => setConfirmAction('capture')}
                          />
                        )}
                      </div>
                    </div>
                  )}
              </div>
            ))}
      </div>

      {/* Invoice Details Side Panel */}
      <SidePaneModel
        open={isInvoicePanelOpen && Boolean(selectedInvoice)}
        title={t('invoiceDetails', { defaultValue: 'Invoice Details' })}
        width="42vw"
        preventQueryClear
        closePanel={() => {
          setIsInvoicePanelOpen(false);
        }}
        content={
          selectedInvoice ? (
            <InvoiceSidePanel
              invoice={selectedInvoice}
              onClose={() => {
                setIsInvoicePanelOpen(false);
              }}
            />
          ) : (
            <div />
          )
        }
      />

      {/* Confirmation Dialog for Release */}
      <ConfirmBox
        open={confirmAction === 'release'}
        closeModal={() => !actionLoading && setConfirmAction(null)}
        title={t('confirmReleaseTitle')}
        subTitle={t('confirmReleaseSubtitle')}
        conformSubmit={handleExecuteRelease}
        submitLabel={t('release')}
        loading={actionLoading}
      />

      {/* Confirmation Dialog for Capture */}
      <ConfirmBox
        open={confirmAction === 'capture'}
        closeModal={() => !actionLoading && setConfirmAction(null)}
        title={t('confirmCaptureTitle')}
        subTitle={t('confirmCaptureSubtitle', {
          amount: getNestedValue('emspFees.finalAmount', false, true),
        })}
        conformSubmit={handleExecuteCapture}
        submitLabel={t('capture')}
        loading={actionLoading}
      />
    </div>
  );
}

export default EmspCdrSidePanel;
